// The enum unions are GENERATED from the database by
// scripts/gen-enum-types.ts. Hand-copying them is what let `Channel` and
// `Role` drift within a week of the schema changing (R-40).
import type {
  Channel,
  GarmentType,
  Fulfilment,
  ProofStatus,
  Settlement,
  PushPlatform,
  OrderStatus,
  QuoteStatus,
} from './database.enums';

export type {
  Channel,
  GarmentType,
  Fulfilment,
  ProofStatus,
  Settlement,
  PushPlatform,
  OrderStatus,
  QuoteStatus,
};





export interface ColorOption {
  name: string;
  hex: string;
}

export interface Brand {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  accent_hex: string;
  sort: number;
  active: boolean;
}

/** A blank: the physical garment decoration is applied to. */
export interface Product {
  id: string;
  name: string;
  category: string;
  type: GarmentType;
  base_price_cents: number;
  colors: ColorOption[];
  sizes: string[];
  active: boolean;
}

/** `print_methods` row, cents on the wire, dollars in the engine. */
export interface PrintMethodRow {
  key: string;
  name: string;
  blurb: string | null;
  moq: number;
  max_colours: number | null;
  turnaround: string;
  setup_per_colour_per_location_cents: number;
  setup_per_location_cents: number;
  print_per_location_cents: number;
  extra_colour_cost_cents: number;
  specialty: string | null;
  sort: number;
  active: boolean;
}

export interface ReadyDesign {
  id: string;
  name: string;
  image_url: string;
  sort: number;
  active: boolean;
}

export interface Placement {
  pos_x: number;
  pos_y: number;
  scale: number;
}

/** A retail item: blank + design + decoration, published to the storefront. */
export interface PublishedProduct {
  id: string;
  title: string;
  brand_id: string;
  product_id: string;
  type: GarmentType;
  color_name: string;
  color_hex: string;
  design_url: string;
  method_key: string;
  colours: number;
  locations: string[];
  pos_x: number | null;
  pos_y: number | null;
  scale: number | null;
  base_cost_cents: number;
  retail_price_cents: number;
  fulfilment: Fulfilment;
  stock_qty: number | null;
  active: boolean;
  created_at: string;
}

export interface Address {
  line1: string;
  line2?: string;
  suburb: string;
  state: string;
  postcode: string;
}

export interface Profile {
  user_id: string;
  full_name: string | null;
  company: string | null;
  phone: string | null;
  default_address: Address | null;
  marketing_push_opt_in: boolean;
  created_at: string;
}

export interface Artwork {
  id: string;
  user_id: string | null;
  r2_key: string;
  url: string;
  original_name: string | null;
  mime: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  created_at: string;
}

export interface OrderItem {
  id: string;
  order_id: string;
  published_id: string | null;
  title: string;
  config: Record<string, unknown> | null;
  qty: number;
  unit_price_cents: number;
  line_total_cents: number;
  /**
   * The product's photos for this line, colourway first, worked out on the
   * server from `config.variant_id`. Empty when the line names no product,
   * which is true of the lines imported from Shopify's history. Spec 008 D-3.
   */
  photos?: Array<{ url: string; alt: string }>;
}

export interface OrderEvent {
  id: string;
  order_id: string;
  status: OrderStatus;
  note: string | null;
  created_at: string;
}

export interface Order {
  id: string;
  ref: string;
  user_id: string;
  /** NO DEFAULT. Set server-side from the authenticated request. Constitution §3. */
  channel: Channel;
  /**
   * How the customer reached the studio: `phone`, `instagram`, `referral`.
   *
   * NOT the same question as `channel`, which says which machine wrote the row.
   * A telephone order is `channel: 'manual'` and `source: 'phone'`. Null on the
   * 12,656 orders imported from Shopify, because nobody recorded it and a
   * guess would be worse than a blank. Spec 006.
   */
  source: string | null;
  status: OrderStatus;
  subtotal_cents: number;
  shipping_cents: number;
  gst_cents: number;
  total_cents: number;
  /** Frozen at checkout. Never derived retroactively from Stripe amounts. */
  commission_base_cents: number;
  settlement: Settlement;
  stripe_payment_intent_id: string | null;
  quote_id: string | null;
  app_originated: boolean;
  shipping_address: Address;
  created_at: string;
  items: OrderItem[];
  events: OrderEvent[];
  /**
   * Does any decoration work exist for this order?
   *
   * Optional because the fixtures have no jobs and a customer's own order read
   * does not need it. Only the pipeline board asks, so that a stock sale in the
   * "In production" column can say why the job board is empty. R-196.
   */
  has_job?: boolean;
  /**
   * The order's jobs, and how many are complete. Spec 015 FR-1530: the order
   * follows its jobs, so the Pipeline card says how far they have gone.
   */
  jobs_total?: number;
  jobs_complete?: number;
  /**
   * The order's lines as stock, for the Pipeline card: what the goods are,
   * before anybody opens the order. Only the board asks. No cost or price.
   */
  stock_lines?: PipelineStockLine[];
}

/** One line of a Pipeline card. Null where a line has no stock item (decoration, free text). */
export interface PipelineStockLine {
  order_item_id: string;
  title: string;
  qty: number;
  item_id: string | null;
  code: string | null;
  product: string | null;
  colour: string | null;
  size: string | null;
}

export interface QuoteContact {
  name: string;
  company?: string;
  email: string;
  phone?: string;
}

export interface Proof {
  id: string;
  quote_id: string;
  round: number;
  image_url: string;
  note: string | null;
  status: ProofStatus;
  response_note: string | null;
  responded_at: string | null;
  created_at: string;
}

export interface Quote {
  id: string;
  ref: string;
  user_id: string | null;
  channel: Channel;
  /** Immutable after insert. Propagates to any order the quote converts into. */
  app_originated: boolean;
  contact: QuoteContact;
  notes: string | null;
  product_id: string;
  method_key: string;
  color: string | null;
  size: string | null;
  qty: number;
  colours: number;
  locations: string[];
  artwork_id: string | null;
  ready_design_id: string | null;
  design_url: string | null;
  pos_x: number | null;
  pos_y: number | null;
  scale: number | null;
  est_unit_price_cents: number | null;
  est_setup_cents: number | null;
  est_total_cents: number | null;
  unit_price_cents: number | null;
  setup_cents: number | null;
  total_cents: number | null;
  lead_time: string | null;
  status: QuoteStatus;
  created_at: string;
  proofs: Proof[];
}

export interface CommissionLedgerRow {
  id: string;
  order_id: string;
  order_ref: string;
  channel: Channel;
  base_cents: number;
  /** Basis points. 1200 = 12%. */
  rate_bp: number;
  amount_cents: number;
  settlement: Settlement;
  stripe_payment_intent_id: string | null;
  refund_adjust_cents: number;
  invoiced_month: string | null;
  created_at: string;
}

export interface PartnershipConfig {
  launch_date: string | null;
  rate_bp: number;
  active: boolean;
}

export interface Notification {
  id: string;
  user_id: string;
  type: 'proof_ready' | 'order_status' | 'campaign' | 'reorder';
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  sent_at: string;
  read_at: string | null;
}

export interface PushCampaign {
  id: string;
  title: string;
  body: string;
  deep_link: string | null;
  audience: string;
  scheduled_at: string | null;
  sent_at: string | null;
  created_by: string | null;
  delivered_count: number;
}

export interface AuditLogRow {
  id: string;
  actor: string;
  table_name: string;
  pk: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  created_at: string;
}

/**
 * `tenant_config` and the company row behind it. R-140.
 *
 * Who this shop belongs to. The table was seeded on the first day and **nothing
 * read it**: the wordmark said "The Embroidery Shop", the footer said "Obsessed
 * Gymwear" and the email footer said "Shopace Apparel Pty Ltd", all three
 * hard-coded, on the same product, at the same time.
 *
 * One read, one answer, used everywhere a name is shown.
 */
export interface Tenant {
  /** `brand.name`. What the shop is called. */
  name: string;
  /**
   * `brand.system_name`. What the staff call the system itself, beside the
   * shop's mark in the staff header and in the browser tab. Guy named it ESSE,
   * the core of OBSESSED, on 30 October 2026. A customer never sees it.
   */
  system_name: string;
  /** `brand.system_logo_url`. The ESSE artwork. Null draws the name as type. */
  system_logo_url: string | null;
  /** `companies.legal_name`. Who the invoice is from. Not the same thing. */
  legal_name: string;
  accent_hex: string;
  /** Null until the client supplies brand assets: spec 003 Q-15. */
  logo_url: string | null;
  email_from: string;
  domain: string;
  currency: string;
  gst_rate: number;
  timezone: string;
}

/**
 * `xero_config`, spec 004 §3.9. One row, `id` always true.
 *
 * It exists so an account code is a data change rather than a release. Until
 * 3 September 2026 nothing read it, and the only codes the application could
 * reach were `TEMPORARY_CODES` in `xero/documents.ts`, which is the constant
 * that file's own header says must not exist. R-272, T151.
 */
export interface XeroConfig {
  id: boolean;
  sales_account_code: string;
  purchase_account_code: string;
  sales_tax_type: string;
  purchase_tax_type: string;
  invoice_due_days: number;
  read_back_minutes: number;
  codes_are_temporary: boolean;
  updated_at: string;
}

/** `site_config`, FR-15. Soft config lives in tables, not env vars. */
export interface SiteConfig {
  /** Store badges in the footer and confirmation page, plus the email block. */
  apps_live: boolean;
  shipping_flat_cents: number;
  free_shipping_threshold_cents: number;
  /** The app-exclusive launch offer code. Web checkout rejects it. AC-6.3. */
  app_only_promo_code: string | null;
  app_only_promo_label: string | null;
}

export interface CartLine {
  published_id: string;
  size: string;
  qty: number;
}

/** Allowed order pipeline transitions, guarded server-side regardless of UI. */
export const ORDER_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  // R-33. The order exists before the money does, so the reservation has a
  // parent row and the stock is held across the payment. `expired` is where an
  // abandoned checkout lands when the sweep releases its reservation.
  pending_payment: ['paid', 'expired', 'cancelled'],
  expired: [],
  paid: ['in_production', 'cancelled', 'refunded'],
  in_production: ['quality_check', 'cancelled'],
  quality_check: ['shipped', 'in_production'],
  shipped: ['delivered'],
  delivered: ['refunded'],
  cancelled: [],
  refunded: [],
};

/** Quote pipeline per AC-3.5, with declined / cancelled exits. */
export const QUOTE_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  submitted: ['priced', 'declined', 'cancelled'],
  priced: ['proof_sent', 'declined', 'cancelled'],
  proof_sent: ['approved', 'cancelled'],
  approved: ['deposit_paid', 'cancelled'],
  deposit_paid: ['in_production', 'cancelled'],
  in_production: ['quality_check', 'cancelled'],
  quality_check: ['shipped'],
  shipped: ['completed'],
  completed: [],
  declined: [],
  cancelled: [],
};

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  pending_payment: 'Awaiting payment',
  expired: 'Expired',
  paid: 'Paid',
  in_production: 'In production',
  quality_check: 'Quality check',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  submitted: 'Submitted',
  priced: 'Priced',
  proof_sent: 'Proof sent',
  approved: 'Approved',
  deposit_paid: 'Deposit paid',
  in_production: 'In production',
  quality_check: 'Quality check',
  shipped: 'Shipped',
  completed: 'Completed',
  declined: 'Declined',
  cancelled: 'Cancelled',
};
